import { useState, type ReactNode } from 'react';
import type { CatalogV2ListRow } from '@skupreme/api-contracts/core';
import { formatWhen } from '@features/catalog-details/lib/v2';
import { Tooltip, cn } from '@skupreme/shared/ui';
import { formatAvailable } from '../../lib/v2';

type ProviderAvailability = NonNullable<
  CatalogV2ListRow['available_by_provider']
>[number];

const providerLabel = ({ display_name, provider_type }: ProviderAvailability) =>
  display_name || provider_type || 'Other';

/* EN-4572: groups holding 0 units are omitted; negative reports stay, so the shown lines always add up to Total. */
const holders = (row: CatalogV2ListRow) =>
  (row.available_by_provider ?? []).filter(group => group.available !== 0);

/**
 * The provider type's brand logo (logo_url from the API); its initial when it has none or the image fails to load. In the
 * dark theme the logo sits on a light tile, as ChannelMark's DARK_LOGO_TILE does: several brand marks are dark on transparent.
 */
function ProviderLogo({ group }: { group: ProviderAvailability }) {
  const [failed, setFailed] = useState(false);
  const src = group.logo_url;

  if (!src || failed) {
    return (
      <span className="sk:flex sk:size-4 sk:shrink-0 sk:items-center sk:justify-center sk:rounded-full sk:bg-fill-secondary sk:text-[9px] sk:font-bold sk:text-fg-secondary">
        {providerLabel(group)[0].toUpperCase()}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt=""
      className="sk:size-4 sk:shrink-0 sk:rounded-full sk:object-contain sk:[[data-theme=dark]_&]:bg-fg"
      onError={() => setFailed(true)}
    />
  );
}

/** One line of the popover: an optional mark, a label, the quantity right-aligned. */
function Line({
  mark,
  label,
  qty,
  className,
}: {
  mark?: ReactNode;
  label: string;
  qty: number | null;
  className?: string;
}) {
  return (
    <div className={cn('sk:flex sk:items-center sk:gap-2', className)}>
      {mark}
      <span className="sk:min-w-0 sk:flex-1 sk:truncate">{label}</span>
      <span className="sk:tabular-nums">{formatAvailable(qty)}</span>
    </div>
  );
}

/* The units per provider (and per location under a provider with two or more), the total, and how fresh it is. */
function AvailabilityPopover({
  row,
  groups,
}: {
  row: CatalogV2ListRow;
  groups: ProviderAvailability[];
}) {
  /* The oldest as_of: the total is no fresher than its stalest part. ISO strings sort by time. */
  const asOf = groups
    .map(group => group.as_of)
    .filter((value): value is string => !!value)
    .sort()[0];

  return (
    <div className="sk:flex sk:min-w-48 sk:flex-col sk:gap-1">
      {groups.map(group => (
        <div key={group.connection_id ?? providerLabel(group)}>
          <Line
            mark={<ProviderLogo group={group} />}
            label={providerLabel(group)}
            qty={group.available}
          />
          {(group.locations?.length ?? 0) > 1
            ? group.locations?.map(location => (
                <Line
                  key={location.location_id}
                  label={location.name || 'Unnamed location'}
                  qty={location.available}
                  className="sk:pl-6 sk:text-fg-tertiary"
                />
              ))
            : null}
        </div>
      ))}
      <Line
        label="Total"
        qty={row.available}
        className="sk:border-t sk:border-border sk:pt-1 sk:font-medium"
      />
      {asOf ? (
        <span className="sk:text-xs sk:text-fg-tertiary">
          As of {formatWhen(asOf)}
        </span>
      ) : null}
    </div>
  );
}

/** The total available; hover or focus it to see the units at each storage provider. */
export function CatalogAvailability({ row }: { row: CatalogV2ListRow }) {
  const total = formatAvailable(row.available);
  const groups = holders(row);

  if ((row.available ?? 0) <= 0 || !groups.length) {
    return <span>{total}</span>;
  }

  const label = `${total} available: ${groups
    .map(group => `${providerLabel(group)} ${formatAvailable(group.available)}`)
    .join(', ')}`;

  return (
    <Tooltip
      placement="bottom"
      content={<AvailabilityPopover row={row} groups={groups} />}
    >
      <span
        role="img"
        aria-label={label}
        className="sk:underline sk:decoration-dotted sk:decoration-fg-tertiary sk:underline-offset-4"
      >
        {total}
      </span>
    </Tooltip>
  );
}
